import {
  Box,
  Card,
  ColorSwatch,
  ScrollArea,
  Separator,
  Text,
} from "@salestrak/ui";
import { PageHeader, PageHeaderBack, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { WebhookActionsMenu } from "./webhook-actions-menu";
import {
  useWebhookTargetLabel,
  type WebhookItem,
} from "#app/hooks/settings/use-webhook-mutations";
import { formatDate } from "#app/lib/utils/format-date";

const TRIGGER_LABELS: Record<string, string> = {
  issueStageChange: "Project Stage Change",
  issueCreatedOnStage: "Project Created On Stage",
  issueDeleted: "Project Deleted",
  issueTrashed: "Project Trashed",
};

interface WebhookDetailViewProps {
  webhook: WebhookItem;
  onBack: () => void;
  onEdit: () => void;
  onToggleArchived: () => void;
  onDelete: () => void;
}

export function WebhookDetailView({
  webhook,
  onBack,
  onEdit,
  onToggleArchived,
  onDelete,
}: WebhookDetailViewProps) {
  const targetLabel = useWebhookTargetLabel(webhook.when);

  const dateLabel = webhook.creationDate ? formatDate(webhook.creationDate) : null;

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="API" onClick={onBack} />
          <PageHeaderTitle
            adornment={
              <WebhookActionsMenu
                archived={webhook.archived}
                onEdit={onEdit}
                onToggleArchived={onToggleArchived}
                onDelete={onDelete}
              />
            }
          >
            {webhook.name}
          </PageHeaderTitle>
          {dateLabel ? (
            <PageHeaderSubtitle>Created {dateLabel}</PageHeaderSubtitle>
          ) : null}
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
        <Box fill="width" maxWidth={800}>
          <Card>
            <Box padding="md" paddingBottom="none">
              <Text weight="medium" size="sm">Delivery Status</Text>
              <Box direction="row" gap="sm" align="center">
                <ColorSwatch
                  color={webhook.archived ? "grey" : "green"}
                  size="xs"
                  rounded="full"
                />
                <Text size="sm" variant="muted">{webhook.archived ? "Disabled" : "Enabled"}</Text>
              </Box>
            </Box>

            <Box paddingX="md"><Separator /></Box>

            <Box padding="md" paddingBottom="none">
              <Text weight="medium" size="sm">Method</Text>
              <Text size="sm" variant="muted">{(webhook.method ?? "post").toUpperCase()}</Text>
            </Box>

            <Box paddingX="md"><Separator /></Box>

            <Box padding="md" paddingBottom="none">
              <Text weight="medium" size="sm">Url</Text>
              <Text size="sm" variant="muted">{webhook.url}</Text>
            </Box>

            <Box paddingX="md"><Separator /></Box>

            <Box padding="md" paddingBottom="none">
              <Text weight="medium" size="sm">Event source</Text>
              <Text size="sm" variant="muted">{webhook.resource}</Text>
            </Box>

            <Box paddingX="md"><Separator /></Box>

            <Box padding="md" paddingBottom={targetLabel ? "none" : undefined}>
              <Text weight="medium" size="sm">Trigger</Text>
              <Text size="sm" variant="muted">
                {TRIGGER_LABELS[webhook.trigger] ?? webhook.trigger}
              </Text>
            </Box>

            {targetLabel ? (
              <>
                <Box paddingX="md"><Separator /></Box>
                <Box padding="md">
                  <Text weight="medium" size="sm">Target</Text>
                  <Text size="sm" variant="muted">{targetLabel}</Text>
                </Box>
              </>
            ) : null}
          </Card>
        </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
